import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import type { WebSocketEvent, ContainerStateEvent, VMStateEvent, LogEvent } from '../types'

interface WebSocketContextType {
  connected: boolean
  lastEvent: WebSocketEvent | null
  subscribe: (types: string[]) => void
  onContainerState: (handler: (event: ContainerStateEvent) => void) => () => void
  onVMState: (handler: (event: VMStateEvent) => void) => () => void
  onLog: (handler: (event: LogEvent) => void) => () => void
}

const WebSocketContext = createContext<WebSocketContextType | null>(null)

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState<WebSocketEvent | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const handlersRef = useRef<{
    containerState: Set<(event: ContainerStateEvent) => void>
    vmState: Set<(event: VMStateEvent) => void>
    log: Set<(event: LogEvent) => void>
  }>({
    containerState: new Set(),
    vmState: new Set(),
    log: new Set(),
  })

  const connect = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`)

    ws.onopen = () => {
      console.log('WebSocket connected')
      setConnected(true)
    }

    ws.onclose = () => {
      console.log('WebSocket disconnected')
      setConnected(false)
      // Reconnect after 3 seconds
      setTimeout(connect, 3000)
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WebSocketEvent
        setLastEvent(data)

        switch (data.type) {
          case 'container_state':
            handlersRef.current.containerState.forEach((handler) =>
              handler(data.data as ContainerStateEvent)
            )
            break
          case 'vm_state':
            handlersRef.current.vmState.forEach((handler) =>
              handler(data.data as VMStateEvent)
            )
            break
          case 'log':
            handlersRef.current.log.forEach((handler) =>
              handler(data.data as LogEvent)
            )
            break
        }
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e)
      }
    }

    wsRef.current = ws
  }, [])

  useEffect(() => {
    connect()
    return () => {
      wsRef.current?.close()
    }
  }, [connect])

  const subscribe = useCallback((types: string[]) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', filters: types }))
    }
  }, [])

  const onContainerState = useCallback((handler: (event: ContainerStateEvent) => void) => {
    handlersRef.current.containerState.add(handler)
    return () => {
      handlersRef.current.containerState.delete(handler)
    }
  }, [])

  const onVMState = useCallback((handler: (event: VMStateEvent) => void) => {
    handlersRef.current.vmState.add(handler)
    return () => {
      handlersRef.current.vmState.delete(handler)
    }
  }, [])

  const onLog = useCallback((handler: (event: LogEvent) => void) => {
    handlersRef.current.log.add(handler)
    return () => {
      handlersRef.current.log.delete(handler)
    }
  }, [])

  return (
    <WebSocketContext.Provider
      value={{
        connected,
        lastEvent,
        subscribe,
        onContainerState,
        onVMState,
        onLog,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
